import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import { useRefetchQuery } from "libs/reactQuery";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Checkbox, Input, Modal } from "ui";
import * as yup from "yup";

import { useCreateWarehouse, useUpdateWarehouse } from "../../apis";
import {
  WarehouseFormModalProps,
  WarehouseFormValues,
} from "./WarehouseFormModal.types";

const WAREHOUSE_FORM_ID = "WAREHOUSE_FORM_ID";

const schema = yup.object({
  name: yup
    .string()
    .trim()
    .required("Warehouse name is required")
    .max(120, "Warehouse name is too long"),
  address: yup.string().trim().max(255, "Address is too long").defined(),
  phoneNumber: yup.string().trim().max(32, "Phone number is too long").defined(),
  openingHours: yup
    .string()
    .trim()
    .max(120, "Opening hours are too long")
    .defined(),
  isReturnPickupPoint: yup.boolean().defined(),
});

const WarehouseFormModal = ({
  isOpen,
  onClose,
  warehouse,
}: WarehouseFormModalProps) => {
  const refetchWarehouses = useRefetchQuery(["warehouses"]);
  const isEdit = !!warehouse;

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<WarehouseFormValues>({
    resolver: yupResolver(schema),
    values: {
      name: warehouse?.name ?? "",
      address: warehouse?.address ?? "",
      phoneNumber: warehouse?.phoneNumber ?? "",
      openingHours: warehouse?.openingHours ?? "",
      isReturnPickupPoint: warehouse?.isReturnPickupPoint ?? false,
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const { mutate: createWarehouse, isLoading: isCreating } = useCreateWarehouse(
    {
      onSuccess: () => {
        refetchWarehouses();
        toast.success(t("Warehouse was created successfully"));
        handleClose();
      },
    },
  );

  const { mutate: updateWarehouse, isLoading: isUpdating } = useUpdateWarehouse(
    {
      onSuccess: () => {
        refetchWarehouses();
        toast.success(t("Warehouse was updated successfully"));
        handleClose();
      },
    },
  );

  const onSubmit = (values: WarehouseFormValues) => {
    const body = {
      name: values.name.trim(),
      address: values.address.trim(),
      phoneNumber: values.phoneNumber.trim(),
      openingHours: values.openingHours.trim(),
      isReturnPickupPoint: values.isReturnPickupPoint,
    };

    if (isEdit) {
      updateWarehouse({ id: warehouse.id, body });
    } else {
      createWarehouse(body);
    }
  };

  return (
    <Modal
      formId={WAREHOUSE_FORM_ID}
      isOpen={isOpen}
      title={isEdit ? "Edit Warehouse" : "New Warehouse"}
      okText={isEdit ? "Save" : "Create"}
      isLoading={isCreating || isUpdating}
      onClose={handleClose}
      onOk={handleSubmit(onSubmit)}
    >
      <Input
        label={t("Warehouse name")}
        placeholder={t("e.g. Main Warehouse")}
        errorMessage={errors.name?.message}
        {...register("name")}
      />

      <Input
        label={t("Address")}
        placeholder={t("e.g. Riviera 2, Espace Plantation, Cocody")}
        errorMessage={errors.address?.message}
        {...register("address")}
      />

      <Input
        label={t("Phone number")}
        errorMessage={errors.phoneNumber?.message}
        {...register("phoneNumber")}
      />

      <Input
        label={t("Opening hours")}
        placeholder={t("e.g. Lun-Sam 9h-18h")}
        errorMessage={errors.openingHours?.message}
        {...register("openingHours")}
      />

      {/* Only one warehouse can hold this — flagging another clears the rest
          server-side, so there is no need to unset the old one first. */}
      <Controller
        control={control}
        name="isReturnPickupPoint"
        render={({ field: { value, onChange } }) => (
          <Checkbox
            checked={value ?? false}
            onChange={(e) => onChange(e.target.checked)}
            label={t("Customers collect returns here")}
          />
        )}
      />
    </Modal>
  );
};

export default WarehouseFormModal;
